import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import {
  NotificationStreamEndReason,
  type GetNotificationsParams,
  type NotificationActionId,
  type NotificationActionRequest,
  type NotificationAudience,
  type NotificationCategory,
  type NotificationFeed,
  type NotificationItem,
  type NotificationUnreadCount,
} from '@gokrew/models/notifications';
import { mergeNotificationPages } from '@/utility/notifications';
import type { NotificationStreamHandlers } from '@/utility/notificationStream';

const PAGE_SIZE = 10;
const RECONNECT_CEILING_MS = 30_000;

type FeedData = InfiniteData<NotificationFeed>;

/**
 * The endpoints this feature needs, already bound to the reader's scope. Apps
 * own their HTTP layer — this lib never talks to the network itself — so each
 * portal builds this from its own `api/notifications.ts`. The company routes
 * are company-scoped and the employee routes resolve the company from the
 * token, which is exactly the difference `createApi` absorbs.
 */
export interface NotificationsApi {
  getFeed: (params: GetNotificationsParams) => Promise<NotificationFeed>;
  getUnreadCount: () => Promise<NotificationUnreadCount>;
  markRead: (id: string) => Promise<unknown>;
  markAllRead: () => Promise<unknown>;
  remove: (id: string) => Promise<unknown>;
  runAction: (
    request: NotificationActionRequest,
    promptValue?: string
  ) => Promise<unknown>;
  openStream: (
    signal: AbortSignal,
    handlers: NotificationStreamHandlers
  ) => Promise<void>;
}

/** What `useNotificationStream` reports back about the live connection. */
export interface NotificationStreamStatus {
  /**
   * The first connect attempt has finished, either way. The REST queries wait
   * on this so the stream is always opened before the feed is fetched, without
   * a failed stream blocking the panel forever.
   */
  hasSettled: boolean;
}

export interface CreateUseNotificationsOptions {
  /** The reader's company. Scopes the cache; may also scope the endpoints. */
  useCompanyId: () => string | null | undefined;
  /** Called with the current company to produce the scope-bound endpoints. */
  createApi: (companyId: string) => NotificationsApi;
  actionInvalidationKeys?: Partial<Record<NotificationCategory, QueryKey[]>>;
}

/** What the panel hands `runAction` — the request, plus what it was for. */
export interface RunNotificationActionVariables {
  request: NotificationActionRequest;
  /** Picks the success copy, and the app keys to invalidate afterwards. */
  category: NotificationCategory;
  /** Picks the success copy — an approval should say what it approved. */
  actionId: NotificationActionId;
  promptValue?: string;
}

/** Only the field the error mapping reads, so `axios` stays out of this lib. */
interface RequestError {
  response?: { status?: number };
}

const feedKey = (companyId?: string | null) => [
  QueryKeys.Notifications,
  companyId,
];

const unreadCountKey = (companyId?: string | null) => [
  QueryKeys.NotificationsUnreadCount,
  companyId,
];

const invalidateNotifications = (
  qc: QueryClient,
  companyId?: string | null
) => {
  qc.invalidateQueries({ queryKey: feedKey(companyId) });
  qc.invalidateQueries({ queryKey: unreadCountKey(companyId) });
};

/**
 * Rewrites one item across every cached feed variant (All, Unread, …), and
 * returns the copy it overwrote so an optimistic patch can be rolled back to
 * what the row actually was.
 */
const patchCachedItem = (
  qc: QueryClient,
  companyId: string | null | undefined,
  id: string,
  patch: Partial<NotificationItem>
) => {
  let previous: NotificationItem | undefined;

  qc.setQueriesData<FeedData>({ queryKey: feedKey(companyId) }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            groups: page.groups.map((group) => ({
              ...group,
              items: group.items.map((item) => {
                if (item.id !== id) return item;
                previous = item;
                return { ...item, ...patch };
              }),
            })),
          })),
        }
      : data
  );

  return previous;
};

/**
 * Factory that creates the in-app notification hooks bound to one portal's
 * company source and API layer, so the feed/badge/stream logic stays in one
 * place while each app keeps owning its own endpoints and routes.
 *
 * Usage:
 *   import { createUseNotifications } from '@gokrew/ui';
 *
 *   export const notificationHooks = createUseNotifications({
 *     useCompanyId: () => useAuth().companyId,
 *     createApi: (companyId) => ({ … }),
 *   });
 */
export function createUseNotifications({
  useCompanyId,
  createApi,
  actionInvalidationKeys,
}: CreateUseNotificationsOptions) {
  /** Resolves the company once per render and binds the endpoints to it. */
  const useScopedApi = () => {
    const companyId = useCompanyId();
    const api = useMemo(
      () => (companyId ? createApi(companyId) : null),
      [companyId]
    );
    return { companyId, api };
  };

  /**
   * The unread badge. Its value is also written directly by the live stream,
   * which sends an authoritative count on every frame.
   */
  function useNotificationsUnreadCount({
    enabled = true,
  }: { enabled?: boolean } = {}) {
    const { companyId, api } = useScopedApi();

    return useQuery({
      queryKey: unreadCountKey(companyId),
      queryFn: () => api!.getUnreadCount(),
      enabled: !!api && enabled,
      select: (res: NotificationUnreadCount) => res.count,
    });
  }

  /**
   * Date-grouped feed. Both filters are server-side — a tab that narrowed the
   * loaded pages client-side would page through the whole history to fill
   * itself — so each one is part of the query key and gets its own cache entry.
   * Pages are merged on the group date because pagination counts items, not
   * groups.
   */
  function useNotificationFeed({
    unreadOnly = false,
    audience,
    enabled = true,
  }: {
    unreadOnly?: boolean;
    audience?: NotificationAudience;
    enabled?: boolean;
  } = {}) {
    const { companyId, api } = useScopedApi();

    const query = useInfiniteQuery({
      queryKey: [...feedKey(companyId), unreadOnly, audience ?? null],
      queryFn: ({ pageParam }) =>
        api!.getFeed({
          page: pageParam,
          limit: PAGE_SIZE,
          unreadOnly,
          audience,
        }),
      initialPageParam: 1,
      getNextPageParam: (last) =>
        last.page < last.totalPages ? last.page + 1 : undefined,
      enabled: !!api && enabled,
    });

    const groups = useMemo(
      () => mergeNotificationPages(query.data?.pages ?? []),
      [query.data]
    );

    return { ...query, groups };
  }

  /** Read state, deletion, and the inline action requests items carry. */
  function useNotificationMutations() {
    const { companyId, api } = useScopedApi();
    const qc = useQueryClient();
    const { t } = useTranslation(TranslationNS.Notification);

    const markRead = useMutation({
      mutationFn: (id: string) => api!.markRead(id),
      // Patched in place instead of refetching so an item does not disappear
      // from the Unread tab under the cursor that just clicked it.
      onMutate: (id) => ({
        previous: patchCachedItem(qc, companyId, id, {
          isRead: true,
          readAt: new Date().toISOString(),
        }),
      }),
      // Rolled back to the copy it overwrote rather than flatly to unread, so
      // it holds for any caller. Without it the badge below refetches the true
      // count while every row still renders as read, and the `isRead` gate the
      // callers use is left reading a lie.
      onError: (_error, id, context) => {
        patchCachedItem(qc, companyId, id, {
          isRead: context?.previous?.isRead ?? false,
          readAt: context?.previous?.readAt ?? null,
        });
        toast.error(t('toast.error'));
      },
      onSettled: () => {
        qc.invalidateQueries({ queryKey: unreadCountKey(companyId) });
      },
    });

    const markAllRead = useMutation({
      mutationFn: () => api!.markAllRead(),
      // No success toast — the badge clearing and the button's own spinner are
      // the feedback. Only a failure is worth interrupting the reader for.
      onSuccess: () => {
        qc.setQueryData(unreadCountKey(companyId), { count: 0 });
      },
      onError: () => toast.error(t('toast.error')),
      onSettled: () => invalidateNotifications(qc, companyId),
    });

    const remove = useMutation({
      mutationFn: (id: string) => api!.remove(id),
      onError: () => toast.error(t('toast.error')),
      onSettled: () => invalidateNotifications(qc, companyId),
    });

    const runAction = useMutation({
      mutationFn: ({ request, promptValue }: RunNotificationActionVariables) =>
        api!.runAction(request, promptValue),
      // Copy is per category and action, so an approval says what it approved
      // instead of only acknowledging the click. i18next takes the first key
      // that resolves, so a category only needs an entry where the generic
      // wording is not good enough.
      onSuccess: (_data, { category, actionId }) =>
        toast.success(
          t([
            `toast.action.${category}.${actionId}`,
            `toast.action_generic.${actionId}`,
            'toast.action_success',
          ])
        ),
      onError: (error: RequestError) => {
        const status = error.response?.status;
        // The domain endpoint rejects an already-decided request with 409/400,
        // so that pair means somebody already acted, not that the call broke.
        toast.error(
          status === 409 || status === 400
            ? t('toast.action_stale')
            : t('toast.error')
        );
      },
      // Settled, not success: a stale 409/400 means someone else already acted,
      // so the domain's screens are out of date either way.
      onSettled: (_data, _error, { category }) => {
        invalidateNotifications(qc, companyId);
        actionInvalidationKeys?.[category]?.forEach((queryKey) =>
          qc.invalidateQueries({ queryKey })
        );
      },
    });

    return { markRead, markAllRead, remove, runAction };
  }

  /**
   * Holds the live SSE stream open for the whole app. Mount this ONCE — the
   * backend caps a user at 3 concurrent streams per company and evicts the
   * oldest beyond that.
   *
   * The stream never replays what was missed, so the `ready` frame refetches
   * the feed — that refetch, not the call order, is what closes the gap:
   * `hasSettled` is already true while the company is still resolving, so the
   * first REST fetch can race the connect. Frames only set the badge and
   * invalidate the feed; items are never spliced in by hand, which keeps
   * `unreadCount` authoritative and dedupe on `id` free.
   *
   * No language goes on the connection, and a language switch must NOT reopen
   * it: the copy on its frames is ignored here (the panel re-reads over REST,
   * which does send the language), so reopening would only spend one of those
   * three slots and evict this user's own oldest tab.
   */
  function useNotificationStream(enabled = true) {
    const { companyId, api } = useScopedApi();
    const qc = useQueryClient();

    const [status, setStatus] = useState<NotificationStreamStatus>({
      hasSettled: false,
    });

    useEffect(() => {
      if (!api || !enabled) {
        // Nothing will open, so the REST queries must not keep waiting on it.
        setStatus({ hasSettled: true });
        return;
      }

      let controller: AbortController | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
      let attempt = 0;
      let stopped = false;
      let disposed = false;

      const setUnreadCount = (count: number) =>
        qc.setQueryData(unreadCountKey(companyId), { count });

      const connect = async () => {
        // A fresh controller per attempt — an aborted one can never be reused.
        const attemptController = new AbortController();
        controller = attemptController;

        try {
          await api.openStream(attemptController.signal, {
            onReady: (frame) => {
              attempt = 0;
              setUnreadCount(frame.unreadCount);
              qc.invalidateQueries({ queryKey: feedKey(companyId) });
              setStatus({ hasSettled: true });
            },
            onNotification: (frame) => {
              setUnreadCount(frame.unreadCount);
              qc.invalidateQueries({ queryKey: feedKey(companyId) });
            },
            onEnd: (frame) => {
              // A newer tab took this user's slot. Reconnecting would only
              // evict somebody else, so this tab gives up its stream and falls
              // back to refetching whenever the panel is opened.
              if (
                frame.reason ===
                NotificationStreamEndReason.ReplacedByNewerConnection
              ) {
                stopped = true;
              }
              attemptController.abort();
            },
          });
        } catch (error) {
          // A 503 (at capacity, shutting down) or a dropped connection is worth
          // another try — the REST feed covers the gap meanwhile. A rejected
          // token, a company this user cannot read, or a route that is gone
          // never will be, and would repeat every 30s for the life of the tab.
          const status = (error as { status?: number })?.status;
          if (status === 401 || status === 403 || status === 404)
            stopped = true;
        }

        if (disposed) return;

        setStatus({ hasSettled: true });
        if (stopped) return;

        attempt += 1;
        reconnectTimer = setTimeout(
          connect,
          Math.min(RECONNECT_CEILING_MS, 2 ** attempt * 1000)
        );
      };

      connect();

      return () => {
        stopped = true;
        disposed = true;
        clearTimeout(reconnectTimer);
        controller?.abort();
      };
    }, [api, companyId, enabled, qc]);

    return status;
  }

  /** Refetches the feed and the badge — used when the panel is opened. */
  function useRefreshNotifications() {
    const companyId = useCompanyId();
    const qc = useQueryClient();

    return useCallback(
      () => invalidateNotifications(qc, companyId),
      [qc, companyId]
    );
  }

  return {
    useNotificationsUnreadCount,
    useNotificationFeed,
    useNotificationMutations,
    useNotificationStream,
    useRefreshNotifications,
  };
}

/** The shape `createUseNotifications` returns — what `createNotifications` takes. */
export type CreatedNotificationHooks = ReturnType<
  typeof createUseNotifications
>;
